import { DateTime } from 'luxon'
import type { Section } from '../models/Section'
import type { SectionAssignment } from '../models/SectionAssignment'

export function calculateInitialExpiresAt({
  section,
  assignment,
}: {
  section: Section
  assignment: SectionAssignment
}) {
  const expiresAtRaw = assignment.isEmpty
    ? undefined
    : assignment.data.expiresAt
  const expiresAt = expiresAtRaw ? DateTime.fromJSDate(expiresAtRaw) : undefined

  const { sectionEndDate } = section.data
  const sectionEndDateTime = sectionEndDate
    ? DateTime.fromJSDate(sectionEndDate)
    : undefined
  // if this is first assignment, don't set initial view for expiredDate
  if (!expiresAt) {
    const nowPlusWeek = DateTime.now().plus({ weeks: 1 })
    const sectionStartDate = section.data.sectionStartDate
    if (sectionStartDate) {
      const sectionStartDatePlusOneWeek = DateTime.fromJSDate(
        sectionStartDate
      ).plus({ weeks: 1 })

      const largerDate =
        nowPlusWeek < sectionStartDatePlusOneWeek
          ? sectionStartDatePlusOneWeek
          : nowPlusWeek

      if (!sectionEndDateTime) return largerDate.set({ hour: 13, minute: 0 })

      const sectionEndDateTimeMinusOneDay = sectionEndDateTime.minus({
        days: 1,
      })

      // Return smaller of the two dates with sectionEndDate minus 1 day
      const finalDate = (
        largerDate < sectionEndDateTimeMinusOneDay
          ? largerDate
          : sectionEndDateTimeMinusOneDay
      ).set({ hour: 13, minute: 0 })

      if (isInFuture(finalDate)) return finalDate
    }

    return undefined
  }

  // set initial to 1 week after last expiresAt
  const expiresAtPlusOneWeek = expiresAt.plus({
    weeks: 1,
  })

  // if our new expiresAt is before section end date, use for initial view
  if (
    (!sectionEndDate ||
      expiresAtPlusOneWeek < DateTime.fromJSDate(sectionEndDate)) &&
    isInFuture(expiresAtPlusOneWeek)
  ) {
    return expiresAtPlusOneWeek
  }

  if (!sectionEndDateTime) return undefined

  // if one week is after section end date, just use the raw expiresAt
  if (expiresAt < sectionEndDateTime && isInFuture(expiresAt)) return expiresAt

  // else set initial view to 1 day before the section end date, but same time
  const sectionEndDateTimeMinusOneDay = sectionEndDateTime
    .minus({ days: 1 })
    .set({ hour: expiresAt.hour, minute: expiresAt.minute })

  if (isInFuture(sectionEndDateTimeMinusOneDay))
    return sectionEndDateTimeMinusOneDay

  return undefined
}

/**
 * expiresAt always needs to be in the future, so add a convenience function
 * to validate this before every return
 */
function isInFuture(date: DateTime) {
  return date > DateTime.now()
}
